import { useEffect, useState } from "react";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { Box, Button, Popover } from "@mui/material";
import {
  CalendarIconNew,
  CloseIcon,
  DownNewIcon,
  FilterCloseIcon,
  InputSearchIcon,
  ReminderOutlineIcon,
  UpNewIcon,
} from "../../../assets/icons/icon";
import styles from "./styles.module.css";
import FilterChips from "./FilterChips";
import FilterPopoverContent from "./FilterPopoverContent";
import { buildConfigFromLegacy } from "./utils";
import { useAppTableFilters } from "./useAppTableFilters";
import { UBButton, UBInput } from "../../../Libraries";
import RangePicker from "../RangePicker";
import { DownloadIconBlack } from "../../../assets/icons/icon";
import { formatString } from "../../../utils";
import { DEFAULT_DATE_FILTERS_REPORTS, sentenceCase } from "../../../constants";

/**
 * Config-driven table filters. Use filtersConfig + filterState + onFilterChange (and optionally appliedChips + onRemoveChip).
 * Legacy: pass filters (string[]) + filterOptions + selected + onSelectionChange.
 */
const AppTableFilters = ({
  filtersConfig: configProp,
  filterState: stateProp,
  onFilterChange,
  appliedChips = [],
  onRemoveChip,
  filters,
  filterOptions = {},
  selected = {},
  onSelectionChange,
  categories,
  exportConfig,
  dueDateConfig,
  sendReminderConfig,
  isResetFilter,
  setIsResetFilter,
  children,
}) => {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isDueDateOpen, setIsDueDateOpen] = useState(false);
  const {
    filtersConfig,
    filterState,
    activeId,
    anchorEl,
    isOpen,
    activeConfig,
    handleOpen,
    handleClose,
    normalizedOptions,
    selectedSet,
    selectedSingle,
    amountLocal,
    setAmountLocal,
    searchQuery,
    setSearchQuery,
    handleMultiSelect,
    handleSingleSelect,
    handleAmountApply,
    handleKeywordsChange,
    handleDateApply,
    selectedDate,
  } = useAppTableFilters({
    filtersConfig: configProp,
    filterState: stateProp,
    onFilterChange,
    filters,
    filterOptions,
    selected,
    onSelectionChange,
    buildConfigFromLegacy,
    categories,
  });

  const isWide = activeConfig?.type === "amount";
  const isDate = activeConfig?.type === "date";
  const popoverPaperClass = isDate
    ? `${styles.popoverPaper} ${styles.popoverPaperDate}`
    : isWide
      ? `${styles.popoverPaper} ${styles.popoverPaperWide}`
      : styles.popoverPaper;

  useEffect(() => {
    if (isResetFilter) {
      setIsResetFilter(false);
    }
  }, [isResetFilter]);
  return (
    <Box className={styles.AppTableFilters}>
      <Box className={styles.leftSection}>
        {filtersConfig.map((cfg) => {
          if (cfg.type === "keywords") {
            const keywordsValue = filterState[cfg.id] ?? "";
            return (
              <Box key={cfg.id} className={styles.keywordsSearchWrapper}>
                <UBInput
                  inputId={`filter_search_${cfg.id}`}
                  name={cfg.id}
                  label=""
                  value={keywordsValue}
                  placeholder={cfg.placeholder || cfg.label || "Search"}
                  onChange={(name, text) => handleKeywordsChange(cfg.id, text)}
                  inputClassname={styles.keywordsSearchInput}
                  baseClassname={styles.keywordsSearchBase}
                  prefix={<InputSearchIcon />}
                  suffix={keywordsValue ? <CloseIcon /> : null}
                  handleSuffix={() => handleKeywordsChange(cfg.id, "")}
                  disabled={cfg.disabled}
                />
              </Box>
            );
          }

          const isActive = activeId === cfg.id;

          let isFilterApplied = appliedChips.find(
            (chip) => (chip.id ? chip.id === cfg.id : chip.label == cfg.label),
          );

          return (
            <Button
              key={cfg.id}
              variant="outlined"
              disableRipple
              disabled={cfg.disabled}
              className={`${styles.filterButton} ${isActive || isFilterApplied ? styles.active : ""}`}
              onClick={(e) => handleOpen(cfg.id, e)}
              endIcon={
                <span className={styles.filterButtonIcon}>
                  {isActive ? (
                    <UpNewIcon />
                  ) : isFilterApplied ? (
                    <FilterCloseIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        triggerLogEvent(analytics, "filter_chip_removed", { filter_id: isFilterApplied?.label });
                        onRemoveChip(isFilterApplied);
                        if (isFilterApplied?.label?.toLowerCase() == "amount") {
                          setAmountLocal({
                            min: "",
                            max: "",
                          });
                        }
                      }}
                    />
                  ) : (
                    <DownNewIcon />
                  )}
                </span>
              }
              sx={{ textTransform: "none", minWidth: 0 }}
            >
              <span
                className={styles.filterButtonLabel}
                title={
                  isFilterApplied
                    ? isFilterApplied?.count > 1
                      ? `${isFilterApplied.label} ${isFilterApplied.value}`
                      : String(isFilterApplied.value ?? "")
                    : undefined
                }
              >
                {isFilterApplied
                  ? isFilterApplied?.count > 1
                    ? `${isFilterApplied.label} ${isFilterApplied.value}`
                    : formatString(isFilterApplied.value)
                  : cfg.id === "customers"
                    ? (cfg.options?.find((opt) => opt.value === stateProp[cfg.id])?.label ?? stateProp[cfg.id])
                    : cfg.label}
              </span>
            </Button>
          );
        })}

        <Popover
          open={isOpen}
          onClose={handleClose}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: { className: popoverPaperClass, elevation: 0 },
          }}
          sx={{ mt: 0.75 }}
        >
          <FilterPopoverContent
            activeConfig={activeConfig}
            normalizedOptions={normalizedOptions}
            selectedSet={selectedSet}
            selectedSingle={selectedSingle}
            amountLocal={amountLocal}
            setAmountLocal={setAmountLocal}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onMultiSelect={handleMultiSelect}
            onSingleSelect={handleSingleSelect}
            onAmountApply={handleAmountApply}
            onDateApply={handleDateApply}
            selectedDate={selectedDate}
            onClose={handleClose}
          />
        </Popover>
      </Box>

      <Box className={styles.rightSection}>
        {sendReminderConfig && (
          <Box className={`${styles.exportWrap} ${styles.sendReminderWrap}`}>
            <UBButton
              id="table_send_reminder_btn"
              title={sendReminderConfig.applyBtnText}
              onClick={() => sendReminderConfig.onExport()}
              loading={sendReminderConfig.loading}
              disabled={sendReminderConfig.loading}
              iconComponent={
                sendReminderConfig.loading
                  ? null
                  : (sendReminderConfig.iconComponent ?? (
                    <ReminderOutlineIcon width="12px" height="12px" />
                  ))
              }
              className={`${styles.exportButton} ${styles.sendReminderButton}`}
            />
          </Box>
        )}

        {dueDateConfig && (
          <Box className={styles.exportWrap}>
            <UBButton
              id="table_due_date_btn"
              title={dueDateConfig.applyBtnText}
              onClick={() => setIsDueDateOpen(!isDueDateOpen)}
              spinnerColor={dueDateConfig.spinnerColor}
              iconComponent={
                dueDateConfig.loading
                  ? null
                  : (dueDateConfig.iconComponent ?? (
                    <CalendarIconNew width="12px" height="12px" />
                  ))
              }
              className={styles.exportButton}
              loading={dueDateConfig.loading}
            />

            {isDueDateOpen ? (
              <RangePicker
                singleSelect={true}
                onFilterDate={(date) => {
                  dueDateConfig.onExport(date);
                }}
                onClearBtn={() => dueDateConfig.onExport("")}
                isOpen={isDueDateOpen}
                setIsOpen={setIsDueDateOpen}
                showDirectPicker={true}
                rangeValue={
                  dueDateConfig.selectedDate ? [dueDateConfig.selectedDate] : []
                }
                prevSelectedDate={dueDateConfig?.selectedDate}
                className={styles.rangePickerWrapper}
                noClearBtn={false}
              />
            ) : null}
          </Box>
        )}

        {exportConfig && (
          <Box className={styles.exportWrap}>
            <UBButton
              id="table_export_btn"
              title={exportConfig.applyBtnText}
              onClick={() => exportConfig?.isDateRangeDontShow ? exportConfig.onExport() : setIsExportOpen(!isExportOpen)}
              spinnerColor={exportConfig.spinnerColor}
              iconComponent={
                exportConfig.loading
                  ? null
                  : (exportConfig.iconComponent ?? (
                    <DownloadIconBlack width="12px" height="12px" />
                  ))
              }
              className={styles.exportButton}
              loading={exportConfig.loading}
              disabled={exportConfig.loading}
            />
            {isExportOpen && (
              <RangePicker
                onFilterDate={(startDate, endDate) => {
                  setIsExportOpen(false);
                  exportConfig.onExport(startDate, endDate);
                }}
                isOpen={isExportOpen}
                setIsOpen={setIsExportOpen}
                showDirectPicker={true}
                rangeValue={[]}
                className={styles.rangePickerWrapper}
                noClearBtn={true}
                applyBtnText={exportConfig.applyBtnText}
                dateFilters={exportConfig.dateFilters ?? DEFAULT_DATE_FILTERS_REPORTS}
              />
            )}
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

export default AppTableFilters;
export { FilterChips };
export { default as CategoryFilterContent } from "./CategoryFilterContent";
export { default as CheckboxFilterContent } from "./CheckboxFilterContent";
export { default as SortFilterContent } from "./SortFilterContent";
export { default as AmountFilterContent } from "./AmountFilterContent";
export { default as DateFilterContent } from "./DateFilterContent";
export { norm, buildConfigFromLegacy, defaultCategoryOptions } from "./utils";
export { useAppTableFilters } from "./useAppTableFilters";
